import isNil from 'lodash/isNil';
import mapKeys from 'lodash/mapKeys';
import omitBy from 'lodash/omitBy';
import snakeCase from 'lodash/snakeCase';
import { create } from 'zustand';

import { segmentTrack } from '@/hooks/analytics/useTracking';
import BuildStatus from '@/models/BuildStatus';
import { PipelineStatus } from '@/models/Pipeline';

const useTestResultsTracking = create(() => ({}) as Record<string, unknown>);

function track(eventName: string, fields: Record<string, unknown>) {
  segmentTrack(
    eventName,
    mapKeys(omitBy(fields, isNil), (_, key) => snakeCase(key)),
  );
}

function getRunnableStatusText(status: BuildStatus | PipelineStatus) {
  switch (status) {
    case BuildStatus.Running:
    case PipelineStatus.RUNNING:
      return 'running';
    case BuildStatus.Success:
    case PipelineStatus.SUCCEEDED:
      return 'successful';
    case BuildStatus.Error:
    case PipelineStatus.FAILED:
      return 'failed';
    case BuildStatus.Aborted:
    case PipelineStatus.ABORTED:
      return 'aborted';
    case BuildStatus.AbortedWithSuccess:
    case PipelineStatus.SUCCEEDED_WITH_ABORT:
      return 'aborted with success';
    default:
      return undefined;
  }
}

function getCommonTrackingFields() {
  const { projectId, buildSlug, pipelineId, buildAttemptId, buildStatus } = useTestResultsTracking.getState();

  return {
    projectId,
    buildSlug,
    pipelineId,
    buildAttemptId,
    buildStatus: getRunnableStatusText(buildStatus as BuildStatus | PipelineStatus),
  };
}

export function updateTestResultsTrackingFields(fields: Record<string, unknown>) {
  useTestResultsTracking.setState(fields);
}

export function replaceTestResultsTrackingFields(fields: Record<string, unknown>) {
  useTestResultsTracking.setState(fields, true);
}

export function trackTestsTabDisplayed(overrides?: Record<string, unknown>) {
  const { testStatusFilter } = useTestResultsTracking.getState();
  track('Tests Tab Displayed', { ...getCommonTrackingFields(), testStatusFilter, ...overrides });
}

export function trackTestsFilterChanged(overrides?: Record<string, unknown>) {
  const { testStatusFilter } = useTestResultsTracking.getState();
  track('Tests Filter Changed', { ...getCommonTrackingFields(), testStatusFilter, ...overrides });
}

export function trackTestSuiteDetailsExpanded(overrides?: Record<string, unknown>) {
  const { testStatusFilter } = useTestResultsTracking.getState();
  track('Test Suite Details Expanded', { ...getCommonTrackingFields(), testStatusFilter, ...overrides });
}

export function trackTestAttachmentDownloadButtonClicked(overrides?: Record<string, unknown>) {
  track('Test Attachment Download Button Clicked', { ...getCommonTrackingFields(), ...overrides });
}

export function trackTestSuiteDetailsViewed(overrides?: Record<string, unknown>) {
  const { testSuite, detailType, testStatusFilter } = useTestResultsTracking.getState();

  track('Test Suite Details Viewed', {
    ...getCommonTrackingFields(),
    testSuite,
    detailType,
    testStatusFilter,
    ...overrides,
  });
}

export function trackTestCaseDetailsViewed(overrides?: Record<string, unknown>) {
  const { testSuite, testCaseName, testStatusFilter } = useTestResultsTracking.getState();

  track('Test Case Details Viewed', {
    ...getCommonTrackingFields(),
    testSuite,
    testCaseName,
    testStatusFilter,
    ...overrides,
  });
}
